import type { SuretyBondFormType } from "@inrev/common";
import type { BondFormTemplate } from "@portal/api/bond-request/agent";
import { useCallback, useId } from "react";
import { useGetFileUrl } from "../../../api";
import type { BondFormData } from "../../../types";
import { BOND_FORM_HEADER, BOND_FORM_TOOLTIP, BondFormSelectInput } from "../../form";
import { Card, PartialIndicator, Spinner, Text } from "../../primitives";
import { Tooltip } from "../../ui/Tooltip";

const DOWNLOAD_BASE_URL = "/v2/files";

export type BondFormSectionProps = {
	value: BondFormData;
	onChange: (value: BondFormData) => void;
	templates: BondFormTemplate[];
	typeLabels: Partial<Record<SuretyBondFormType, string>>;
	allowDelete: boolean;
	allowUpload?: boolean;
	partial: boolean;
	isLoading?: boolean;
};

export const BondFormSection = ({
	value,
	onChange,
	templates,
	typeLabels,
	allowDelete,
	allowUpload,
	partial,
	isLoading = false,
}: BondFormSectionProps) => {
	const { getFileUrl } = useGetFileUrl();
	const tooltipId = useId();
	const showGuidanceTooltip = !isLoading && !value.templateId && value.upload.files.length === 0;

	const handleDownloadFile = useCallback(
		(fileId: string) => {
			getFileUrl({
				baseUrl: `${DOWNLOAD_BASE_URL}/${fileId}`,
				queryKey: ["bondFormFile", fileId],
				asDownload: true,
			});
		},
		[getFileUrl],
	);

	return (
		<Card
			elevation="raised"
			className="rounded-xl"
			id="bond-form-section"
			data-testid="bond-request-submitted-form-card"
		>
			<Card.Body className="p-6">
				<div className="flex flex-col w-full space-y-5">
					<div className="flex items-center justify-between w-full">
						<div className="flex items-center gap-x-2">
							<Text type="title" color="primary">
								{BOND_FORM_HEADER}
							</Text>
							{showGuidanceTooltip && (
								<Tooltip
									id={tooltipId}
									content={BOND_FORM_TOOLTIP}
									condensed
									ariaLabel="Bond form guidance"
								/>
							)}
						</div>
						{!isLoading && partial && (
							<PartialIndicator active className="mt-[1px]" testId="bond-form-partial-indicator" />
						)}
					</div>
					{isLoading ? (
						<div
							className="flex items-center justify-center h-[92px] text-tertiary"
							data-testid="bond-form-loading"
						>
							<Spinner size="lg" />
						</div>
					) : (
						<BondFormSelectInput
							value={value}
							onChange={onChange}
							onDownloadFile={handleDownloadFile}
							downloadUrl={DOWNLOAD_BASE_URL}
							templates={templates}
							typeLabels={typeLabels}
							allowDelete={allowDelete}
							allowUpload={allowUpload}
							className="max-w-full"
						/>
					)}
				</div>
			</Card.Body>
		</Card>
	);
};
